import { Link, useNavigate } from 'react-router-dom'
import { LogOut, User, Search, Bell, Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { OrganizationSwitcher } from './organization-switcher'
import { useAuthStore } from '@/stores/auth-store'
import { useOrganizationStore } from '@/stores/organization-store'

interface HeaderProps {
  title: string
  onToggleSidebar: () => void
}

export function Header({ title, onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const { clearContext } = useOrganizationStore()

  const handleLogout = () => {
    clearContext()
    logout()
    navigate('/login')
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <header className="h-16 bg-popover/80 backdrop-blur-lg border-b border-border/50 flex items-center justify-between px-6 sticky top-0 z-40">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {/* Mobile menu toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          className="md:hidden text-gray-400 hover:text-white hover:bg-white/5"
        >
          <Menu className="w-5 h-5" />
        </Button>

        {/* Page Title */}
        {/* TRUNCA EM VEZ DE EMPURRAR. Com `whitespace-nowrap` e sem `min-w-0`,
            um título longo empurrava o seletor de organização e o avatar para
            fora da tela no mobile. */}
        <h1 className="min-w-0 truncate text-xl font-semibold text-gray-100 mr-4">
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-4 flex-shrink-0">
        {/* A BUSCA SÓ A PARTIR DE `lg`. Ela tem 256px fixos, e no tablet
            (768px, menos os 256 da barra lateral) sobrava tão pouco que o
            título da página era espremido até sumir — medido: largura zero. */}
        <div className="relative hidden lg:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder={t('header.search')}
            className="w-64 pl-10 pr-4 py-2 bg-white/5 border-gray-700/50 rounded-xl text-sm focus:border-indigo-500/50 placeholder-gray-500"
          />
        </div>

        {/* Organization Switcher */}
        <OrganizationSwitcher />

        {/* Notifications */}
        <Button
          variant="ghost"
          size="icon"
          className="relative hidden text-gray-400 hover:text-white hover:bg-white/5 sm:inline-flex"
        >
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-white hover:bg-white/5"
            >
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 glass border-gray-700/50">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none text-gray-100">
                  {user?.full_name || t('header.user')}
                </p>
                <p className="text-xs leading-none text-gray-500">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-700/50" />
            <DropdownMenuItem asChild className="focus:bg-white/5">
              <Link to="/profile" className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                <span>{t('header.myProfile')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-700/50" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive/90 focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t('nav.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
